import apiClient from "src/utils/apiClient";
import type {
  UseCaseConfig,
  WorkspaceTask,
  WorkspaceResult,
  PermissionsMember,
  RoleValue,
  WeeklyAssignmentConfig,
} from "src/components/pages/auditModule/shared/types";
import { ROLE } from "src/components/pages/auditModule/shared/types";

//TODO: Replace placeholder endpoints with real backend routes once Audit V2 APIs are implemented.
export interface AuditModuleTasksResponse {
  tasks: Array<{
    id: string;
    use_case_id: string;
    status: string;
    assigned_to: string | null;
    priority: number;
    created_at: string;
  }>;
}

export interface AuditModuleResultsResponse {
  results: Array<{
    id: string;
    task_id: string;
    auditor_id: string;
    status: string;
    decisions: Record<string, { decision: string }>;
  }>;
}

export interface AuditModuleUseCaseConfigsResponse {
  configs: UseCaseConfig[];
}

export interface AuditModuleSingleConfigResponse {
  config: UseCaseConfig;
}

export interface AuditModuleWorkspaceTaskResponse {
  task: WorkspaceTask | null;
  config: UseCaseConfig | null;
  result: WorkspaceResult | null;
  allTasks: WorkspaceTask[];
}

export interface AuditModuleWorkspaceResultResponse extends WorkspaceResult {}

// Raw shape returned by the audit_access members API (backend PermissionMemberView).
interface PermissionMemberView {
  alias: string;
  displayName: string | null;
  role: string; // "Admin" | "Program Owner" | "Auditor"
  authorizedUseCases: string[];
  elevated: boolean;
}

const API_ROLE_BY_FRONTEND: Record<RoleValue, string> = {
  [ROLE.AUDITOR]: "Auditor",
  [ROLE.OWNER]: "Program Owner",
  [ROLE.ADMIN]: "Admin",
};

const FRONTEND_ROLE_BY_API: Record<string, RoleValue> = {
  Auditor: ROLE.AUDITOR,
  "Program Owner": ROLE.OWNER,
  Admin: ROLE.ADMIN,
};

function toPermissionsMember(v: PermissionMemberView): PermissionsMember {
  return {
    login: v.alias,
    name: v.displayName || v.alias,
    role: FRONTEND_ROLE_BY_API[v.role] ?? ROLE.AUDITOR,
    assigned_use_case_ids: v.authorizedUseCases ?? [],
    elevated: v.elevated,
  };
}

class AuditModuleService {
  async getTasks(): Promise<AuditModuleTasksResponse> {
    const { data } = await apiClient.get("/audit_queue/tasks");
    return data;
  }

  async getResults(taskIds: string[]): Promise<AuditModuleResultsResponse> {
    const { data } = await apiClient.post("/audit_queue/results", { task_ids: taskIds });
    return data;
  }

  // ----------------------------  Config Endpoints  ----------------------------

  async getUseCaseConfigs(): Promise<AuditModuleUseCaseConfigsResponse> {
    const { data } = await apiClient.get("/audit_config/use_cases");
    return data;
  }

  async getUseCaseConfig(useCaseId: string): Promise<AuditModuleSingleConfigResponse> {
    const { data } = await apiClient.get("/audit_config/use_cases/" + useCaseId);
    return data;
  }

  async createUseCaseConfig(config: UseCaseConfig): Promise<AuditModuleSingleConfigResponse> {
    const { data } = await apiClient.post("/audit_config/use_cases", config);
    return data;
  }

  async updateUseCaseConfig(config: UseCaseConfig): Promise<AuditModuleSingleConfigResponse> {
    const { data } = await apiClient.put("/audit_config/use_cases/" + config.id, config);
    return data;
  }

  async getWeeklyAssignment(useCaseId: string, week: string): Promise<WeeklyAssignmentConfig> {
    const { data } = await apiClient.get<{ assignment: WeeklyAssignmentConfig }>(
      "/audit_config/use_cases/" + useCaseId + "/weekly_assignment",
      { params: { week } },
    );
    return data.assignment;
  }

  async saveWeeklyAssignment(
    useCaseId: string,
    assignment: WeeklyAssignmentConfig,
  ): Promise<WeeklyAssignmentConfig> {
    const { data } = await apiClient.put<{ assignment: WeeklyAssignmentConfig }>(
      "/audit_config/use_cases/" + useCaseId + "/weekly_assignment",
      assignment,
    );
    return data.assignment;
  }

  // ----------------------------  Permissions / Access Endpoints  ----------------------------

  async getPermissionMembers(): Promise<PermissionsMember[]> {
    const { data } = await apiClient.get<PermissionMemberView[]>("/audit_access/members");
    return data.map(toPermissionsMember);
  }

  async updateMemberRole(alias: string, role: RoleValue): Promise<PermissionsMember> {
    const { data } = await apiClient.put<PermissionMemberView>("/audit_access/members/" + alias, {
      role: API_ROLE_BY_FRONTEND[role],
    });
    return toPermissionsMember(data);
  }

  async setMemberUseCases(alias: string, useCaseIds: string[]): Promise<PermissionsMember> {
    const { data } = await apiClient.put<PermissionMemberView>(
      "/audit_access/members/" + alias + "/use_cases",
      { useCaseIds },
    );
    return toPermissionsMember(data);
  }

  async deletePermissionMember(alias: string): Promise<void> {
    await apiClient.delete("/audit_access/members/" + alias);
  }

  // ----------------------------  Workspace Endpoints  ----------------------------

  async getWorkspaceTask(taskId: string): Promise<AuditModuleWorkspaceTaskResponse> {
    const { data } = await apiClient.get("/audit_workspace/tasks/" + taskId);
    return data;
  }

  async saveWorkspaceResult(
    result: WorkspaceResult,
    submit: boolean,
  ): Promise<AuditModuleWorkspaceResultResponse> {
    const { data } = await apiClient.post("/audit_workspace/results", { ...result, submit });
    return data;
  }
}

export default new AuditModuleService();
